/**
 * Feature flags: switch a feature off, or on for only some members, without a
 * release.
 *
 * Every flag is declared in the backend code with a title, a description and
 * a built-in setting; this screen lists them as `GET /feature-flags` returns
 * them, writes a change with `PUT /feature-flags/{key}` and puts a flag back
 * on its built-in setting with `DELETE /feature-flags/{key}`. The API asks for
 * a reason on every change and writes it to the audit log, which is where each
 * flag's history below comes from.
 *
 * Every change - off, on, rollout, reset - goes through one dialog that
 * states the setting before and after and asks for the reason, so nothing
 * changes on a single click. Managers can read the list (`operations:read`)
 * but only admins and super admins can change it (`configuration:manage`).
 */

import {
  auditRecordsApiV1InternalAuditRecordsGet,
  featureFlagsApiV1InternalFeatureFlagsGet,
  resetFeatureFlagApiV1InternalFeatureFlagsKeyDelete,
  setFeatureFlagApiV1InternalFeatureFlagsKeyPut,
  type AuditRecordItem,
  type FeatureFlagItem,
} from '@closet-theory/api';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@ui/components/base/badges/badges';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code } from '@ui/console/primitives';
import { TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, currentRole, handleUnauthorized } from './auth';
import { canAccess } from './auth-policy';
import { ActionDialog, Actions, Page, State } from './components';
import { EntityRef, looksLikeUuid } from './references';
import { errorDetail } from './operation-policy';

type Setting = { enabled: boolean; percentage: number };

/** A flag's setting as a sentence: "Off", "On for everyone", "Rolled out to 25% of members". */
export function describeSetting({ enabled, percentage }: Setting): string {
  if (!enabled) return 'Off';
  if (percentage >= 100) return 'On for everyone';
  if (percentage <= 0) return 'On, but for no members (0%)';
  return `Rolled out to ${percentage}% of members`;
}

function settingColor({ enabled, percentage }: Setting): 'success' | 'warning' | 'gray' {
  if (!enabled || percentage <= 0) return 'gray';
  return percentage >= 100 ? 'success' : 'warning';
}

function areaLabel(owner: string): string {
  const words = owner.replaceAll(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : owner;
}

const when = (iso: string) => new Date(iso).toLocaleString();

export function reasonProblem(reason: string): string | undefined {
  const trimmed = reason.trim();
  if (trimmed.length < 3)
    return 'Say why, in at least three characters. It is kept in the history.';
  if (trimmed.length > 1000) return 'Keep the reason under 1,000 characters.';
  return undefined;
}

export function percentageProblem(value: string): string | undefined {
  if (!/^\d{1,3}$/.test(value.trim())) return 'A whole number from 0 to 100.';
  const number = Number(value);
  return number > 100 ? 'A whole number from 0 to 100.' : undefined;
}

/** The value of a `datetime-local` field as an ISO instant, or a reason it cannot be one. */
export function expiryValue(
  value: string,
  now = Date.now(),
): { ok: true; value: string | null } | { ok: false; problem: string } {
  if (!value.trim()) return { ok: true, value: null };
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return { ok: false, problem: 'That is not a date and time.' };
  if (at.getTime() <= now) return { ok: false, problem: 'Pick a time in the future.' };
  return { ok: true, value: at.toISOString() };
}

type Intent = 'turn-off' | 'turn-on' | 'rollout' | 'reset';

function ChangeDialog({
  flag,
  intent,
  onClose,
  onSaved,
  onForbidden,
}: {
  flag: FeatureFlagItem;
  intent: Intent;
  onClose: () => void;
  onSaved: (flag: FeatureFlagItem) => void;
  onForbidden: () => void;
}) {
  const startingPercentage =
    intent === 'turn-on'
      ? flag.default_percentage > 0
        ? flag.default_percentage
        : 100
      : flag.percentage;
  const [percentage, setPercentage] = useState(String(startingPercentage));
  const [expiry, setExpiry] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string>();
  const [saving, setSaving] = useState(false);

  const before: Setting = { enabled: flag.enabled, percentage: flag.percentage };
  const builtIn: Setting = { enabled: flag.default_enabled, percentage: flag.default_percentage };
  const parsedPercentage = percentageProblem(percentage) ? undefined : Number(percentage);
  const after: Setting =
    intent === 'reset'
      ? builtIn
      : intent === 'turn-off'
        ? { enabled: false, percentage: flag.percentage }
        : { enabled: true, percentage: parsedPercentage ?? flag.percentage };
  const choosesShare = intent === 'turn-on' || intent === 'rollout';
  const name = flag.title.toLowerCase();

  const title = {
    'turn-off': `Turn off ${name}?`,
    'turn-on': `Turn on ${name}?`,
    rollout: `Change who gets ${name}?`,
    reset: `Reset ${name} to its built-in setting?`,
  }[intent];
  const confirm = {
    'turn-off': 'Yes, turn it off',
    'turn-on': 'Yes, turn it on',
    rollout: 'Yes, change the rollout',
    reset: 'Yes, reset it',
  }[intent];

  const save = async () => {
    const found =
      (choosesShare ? percentageProblem(percentage) : undefined) ?? reasonProblem(reason);
    const expires = intent === 'reset' ? { ok: true as const, value: null } : expiryValue(expiry);
    if (found || !expires.ok) {
      setProblem(found ?? (expires.ok ? undefined : expires.problem));
      return;
    }
    setSaving(true);
    setProblem(undefined);
    configureAdminApi();
    const result =
      intent === 'reset'
        ? await resetFeatureFlagApiV1InternalFeatureFlagsKeyDelete({
            path: { key: flag.key },
            body: { reason: reason.trim() },
          })
        : await setFeatureFlagApiV1InternalFeatureFlagsKeyPut({
            path: { key: flag.key },
            body: {
              enabled: after.enabled,
              percentage: after.percentage,
              expires_at: expires.value,
              reason: reason.trim(),
            },
          });
    setSaving(false);
    handleUnauthorized(result.response.status);
    if (result.response.status === 403) {
      onForbidden();
      onClose();
      return;
    }
    if (result.error || !result.data) {
      setProblem(
        errorDetail(result.error, 'The change was not saved. Nothing about the flag changed.'),
      );
      return;
    }
    onSaved(result.data);
    onClose();
  };

  return (
    <ActionDialog
      title={title}
      description={flag.description}
      onClose={onClose}
      footer={
        <>
          <Button size="md" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            color={intent === 'turn-off' ? 'primary-destructive' : 'primary'}
            isDisabled={saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : confirm}
          </Button>
        </>
      }
    >
      {choosesShare ? (
        <TextField
          label="Share of members who get it (%)"
          type="number"
          inputMode="numeric"
          min={0}
          max={100}
          step={1}
          value={percentage}
          hint="100 is everyone. A lower number picks the same members every time, and raising it only adds members. Anything not tied to a particular member counts as off below 100."
          onChange={(event) => setPercentage(event.currentTarget.value)}
        />
      ) : null}
      <div className="flex flex-col gap-1 rounded-lg border border-secondary bg-secondary p-3 text-sm">
        <span className="text-secondary">
          <span className="text-tertiary">Now: </span>
          {describeSetting(before)}
        </span>
        <span className="font-medium text-primary">
          <span className="font-normal text-tertiary">After: </span>
          {describeSetting(after)}
        </span>
      </div>
      {intent === 'reset' ? (
        <p className="text-sm text-tertiary">
          This removes the change made here, so the flag follows the setting built into the app
          again. If a later release changes that setting, the flag follows it.
        </p>
      ) : (
        <TextField
          label="Go back to the built-in setting on (optional)"
          type="datetime-local"
          value={expiry}
          hint={`Leave empty to keep this until someone changes it. After this time the flag returns to its built-in setting: ${describeSetting(
            builtIn,
          ).toLowerCase()}.`}
          onChange={(event) => setExpiry(event.currentTarget.value)}
        />
      )}
      <TextAreaField
        label="Reason"
        rows={2}
        value={reason}
        required
        hint="Required. Kept in this flag's history with your name."
        onChange={(event) => setReason(event.currentTarget.value)}
      />
      {problem ? <Callout tone="error" title={problem} /> : null}
    </ActionDialog>
  );
}

function auditSetting(value: unknown): (Setting & { expires_at: string | null }) | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const record = value as Record<string, unknown>;
  if (typeof record['enabled'] !== 'boolean' || typeof record['percentage'] !== 'number')
    return undefined;
  return {
    enabled: record['enabled'],
    percentage: record['percentage'],
    expires_at: typeof record['expires_at'] === 'string' ? record['expires_at'] : null,
  };
}

/** One history entry's change, in words: "Off → Rolled out to 25% of members". */
export function describeChange(
  item: Pick<AuditRecordItem, 'action' | 'previous_value' | 'new_value'>,
): string {
  const before = auditSetting(item.previous_value);
  const after = auditSetting(item.new_value);
  const from = before ? describeSetting(before) : 'Built-in setting';
  if (item.action === 'configuration.feature_flag_reset') {
    return `${from} → Reset to built-in setting`;
  }
  const to = after ? describeSetting(after) : 'not recorded';
  const until = after?.expires_at ? `, until ${when(after.expires_at)}` : '';
  return `${from} → ${to}${until}`;
}

function History({ flagKey }: { flagKey: string }) {
  const [items, setItems] = useState<AuditRecordItem[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(
    async (after?: string) => {
      setLoading(true);
      setError(undefined);
      configureAdminApi();
      const result = await auditRecordsApiV1InternalAuditRecordsGet({
        query: {
          limit: 20,
          resource_type: 'feature_flag',
          resource_id: flagKey,
          cursor: after ?? null,
        },
      });
      setLoading(false);
      handleUnauthorized(result.response.status);
      if (result.response.status === 403) {
        setError('Your role cannot read the audit log, so this history cannot be shown.');
        return;
      }
      if (result.error || !result.data) {
        setError('The history could not be loaded.');
        return;
      }
      const page = result.data;
      setItems((current) => (after ? [...current, ...page.items] : page.items));
      setCursor(page.next_cursor ?? undefined);
    },
    [flagKey],
  );
  useEffect(() => void load(), [load]);

  return (
    <div className="flex flex-col gap-2" aria-label="Change history">
      {items.length ? (
        <ol className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary">
          {items.map((item) => {
            const name = item.actor_name?.trim() || undefined;
            const email = item.actor_email?.trim() || undefined;
            return (
              <li key={item.id} className="flex flex-col gap-1 p-3 text-sm">
                <span className="font-medium text-primary">{describeChange(item)}</span>
                <span className="flex flex-wrap items-center gap-1 text-tertiary">
                  {when(item.occurred_at)} by{' '}
                  {item.actor_id && looksLikeUuid(item.actor_id) ? (
                    <EntityRef
                      kind="staff"
                      id={item.actor_id}
                      label={name ?? email}
                      secondary={name ? email : undefined}
                    />
                  ) : (
                    (name ?? email ?? 'the system')
                  )}
                </span>
                <span className="text-secondary">
                  <span className="text-tertiary">Reason: </span>
                  {item.reason || 'none given'}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}
      <State
        loading={loading && !items.length}
        error={error}
        onRetry={() => void load()}
        empty={false}
      >
        {!items.length ? (
          <p className="text-sm text-tertiary">
            Nobody has changed this flag yet. It has always followed its built-in setting.
          </p>
        ) : null}
      </State>
      {cursor ? (
        <Actions>
          <Button
            size="sm"
            color="secondary"
            isDisabled={loading}
            onClick={() => void load(cursor)}
          >
            Load older changes
          </Button>
        </Actions>
      ) : null}
    </div>
  );
}

function FlagCard({
  flag,
  readOnly,
  onSaved,
  onForbidden,
}: {
  flag: FeatureFlagItem;
  readOnly: boolean;
  onSaved: (flag: FeatureFlagItem) => void;
  onForbidden: () => void;
}) {
  const [intent, setIntent] = useState<Intent>();
  const [showHistory, setShowHistory] = useState(false);
  // Bumped after a save so an open history re-reads and shows the new entry.
  const [historyVersion, setHistoryVersion] = useState(0);
  const headingId = `flag-${flag.key}`;
  const setting: Setting = { enabled: flag.enabled, percentage: flag.percentage };
  const builtIn = describeSetting({
    enabled: flag.default_enabled,
    percentage: flag.default_percentage,
  });
  const stored = flag.stored;

  return (
    <section
      aria-labelledby={headingId}
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={headingId} className="text-md font-semibold text-primary">
              {flag.title}
            </h3>
            <Badge type="pill-color" size="sm" color={settingColor(setting)}>
              {describeSetting(setting)}
            </Badge>
          </div>
          <span className="text-xs text-tertiary">
            {areaLabel(flag.owner)} · <Code>{flag.key}</Code>
          </span>
        </div>
        {readOnly ? null : (
          <Actions>
            {flag.enabled ? (
              <>
                <Button size="sm" color="secondary" onClick={() => setIntent('rollout')}>
                  Change rollout
                </Button>
                <Button
                  size="sm"
                  color="secondary-destructive"
                  onClick={() => setIntent('turn-off')}
                >
                  Turn off
                </Button>
              </>
            ) : (
              <Button size="sm" color="primary" onClick={() => setIntent('turn-on')}>
                Turn on
              </Button>
            )}
            {stored && !stored.expired ? (
              <Button size="sm" color="tertiary" onClick={() => setIntent('reset')}>
                Reset to built-in setting
              </Button>
            ) : null}
          </Actions>
        )}
      </header>

      <p className="text-sm text-secondary">{flag.description}</p>

      <p className="text-sm text-tertiary">
        {flag.source === 'default' ? (
          stored?.expired ? (
            <>
              Back on its built-in setting ({builtIn.toLowerCase()}): the last change, made{' '}
              {when(stored.updated_at)}, ran out
              {stored.expires_at ? ` on ${when(stored.expires_at)}` : ''}.
            </>
          ) : (
            <>Never changed here. It follows its built-in setting: {builtIn.toLowerCase()}.</>
          )
        ) : stored ? (
          <span className="flex flex-wrap items-center gap-1">
            Changed {when(stored.updated_at)}
            {stored.updated_by ? (
              <>
                {' '}
                by <EntityRef kind="staff" id={stored.updated_by} />
              </>
            ) : null}
            .{' '}
            {stored.expires_at
              ? `Goes back to its built-in setting (${builtIn.toLowerCase()}) on ${when(stored.expires_at)}.`
              : `Its built-in setting is: ${builtIn.toLowerCase()}.`}
          </span>
        ) : null}
      </p>

      <div>
        <Button
          size="sm"
          color="link-gray"
          aria-expanded={showHistory}
          onClick={() => setShowHistory((open) => !open)}
        >
          {showHistory ? 'Hide history' : 'Show history'}
        </Button>
      </div>
      {showHistory ? <History key={historyVersion} flagKey={flag.key} /> : null}

      {intent ? (
        <ChangeDialog
          flag={flag}
          intent={intent}
          onClose={() => setIntent(undefined)}
          onForbidden={onForbidden}
          onSaved={(saved) => {
            onSaved(saved);
            setHistoryVersion((version) => version + 1);
          }}
        />
      ) : null}
    </section>
  );
}

export function FeatureFlags() {
  const [items, setItems] = useState<FeatureFlagItem[]>([]);
  const [ttl, setTtl] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [forbidden, setForbidden] = useState(false);
  // Managers read the list; changing a flag needs configuration:manage. A
  // refused change also lands here, in case the role changed under the page.
  const [readOnly, setReadOnly] = useState(
    () => !canAccess(currentRole(), 'admin:privileged-operator'),
  );
  const [saved, setSaved] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    configureAdminApi();
    const result = await featureFlagsApiV1InternalFeatureFlagsGet();
    setLoading(false);
    handleUnauthorized(result.response.status);
    if (result.response.status === 403) {
      setForbidden(true);
      return;
    }
    if (result.error || !result.data) {
      setError(errorDetail(result.error, 'Feature flags could not be loaded.'));
      return;
    }
    setForbidden(false);
    setItems(result.data.items);
    setTtl(result.data.cache_ttl_seconds);
  }, []);
  useEffect(() => void load(), [load]);

  const replace = (next: FeatureFlagItem) => {
    setItems((current) => current.map((item) => (item.key === next.key ? next : item)));
    setSaved(
      `Saved. ${next.title} is now: ${describeSetting(next).toLowerCase()}.${
        ttl ? ` Every server picks it up within ${Math.ceil(ttl)} seconds.` : ''
      }`,
    );
  };

  return (
    <Page
      title="Feature flags"
      purpose="Use this page to switch a feature off, or on for only some members, without waiting for a new release. Every change asks why, and each flag keeps a history of who changed it."
      toolbar={{ onRefresh: () => void load(), refreshing: loading }}
    >
      {forbidden ? (
        <Callout tone="neutral" title="Your role cannot see feature flags.">
          Nothing has gone wrong. Ask an admin if you need to see or change a flag.
        </Callout>
      ) : (
        <>
          {readOnly ? (
            <Callout tone="neutral" title="You can look at these flags but not change them.">
              Only admins and super admins can turn a flag on or off, change its rollout or reset
              it. Ask an admin if a flag needs changing.
            </Callout>
          ) : null}
          {saved ? (
            <p role="status" className="text-sm text-success-primary">
              {saved}
            </p>
          ) : null}
          <State
            loading={loading && !items.length}
            error={error}
            onRetry={() => void load()}
            empty={false}
          >
            {items.length ? (
              <div className="flex flex-col gap-4">
                {items.map((flag) => (
                  <FlagCard
                    key={flag.key}
                    flag={flag}
                    readOnly={readOnly}
                    onSaved={replace}
                    onForbidden={() => setReadOnly(true)}
                  />
                ))}
              </div>
            ) : (
              <Callout tone="neutral" title="There are no feature flags yet.">
                Flags are added by engineers in the code. Once one exists, it shows up here.
              </Callout>
            )}
          </State>
        </>
      )}
    </Page>
  );
}
